import React, { useState, useEffect } from 'react';
import api from '../services/api';

const VisitorStats = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);

    useEffect(() => {
        loadStats();
        // Refresh stats every 5 minutes
        const interval = setInterval(loadStats, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, []);

    const loadStats = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await api.get('/api/visitors/stats');
            setStats(response.data);
            setLastUpdated(new Date());
        } catch (err) {
            console.error('Error loading visitor stats:', err);
            setError('Failed to load visitor statistics. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const formatNumber = (num) => {
        if (num === null || num === undefined) return '0';
        return num.toLocaleString();
    };

    if (loading && !stats) {
        return (
            <div style={{ textAlign: 'center', padding: '40px' }}>
                <div style={{ fontSize: '24px', marginBottom: '16px' }}>⏳</div>
                <p>Loading visitor statistics...</p>
            </div>
        );
    }

    if (error && !stats) {
        return (
            <div style={{ 
                textAlign: 'center', 
                padding: '40px',
                background: '#fee2e2',
                color: '#991b1b',
                borderRadius: '8px',
                margin: '20px 0'
            }}>
                <div style={{ fontSize: '24px', marginBottom: '16px' }}>❌</div>
                <p>{error}</p>
                <button 
                    onClick={loadStats}
                    style={{
                        marginTop: '16px',
                        padding: '8px 16px',
                        background: '#dc2626',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer'
                    }}
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="visitor-stats" style={{ padding: '20px' }}>
            <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '24px'
            }}>
                <h2 style={{ margin: 0, fontSize: '28px', color: '#1e293b' }}>
                    📊 Visitor Statistics
                </h2>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    {lastUpdated && (
                        <span style={{ 
                            fontSize: '14px', 
                            color: '#64748b',
                            fontStyle: 'italic'
                        }}>
                            Last updated: {lastUpdated.toLocaleTimeString()}
                        </span>
                    )}
                    <button 
                        onClick={loadStats}
                        disabled={loading}
                        style={{
                            padding: '8px 16px',
                            background: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            opacity: loading ? 0.6 : 1,
                            fontSize: '14px',
                            fontWeight: '500'
                        }}
                    >
                        {loading ? '🔄 Refreshing...' : '🔄 Refresh'}
                    </button>
                </div>
            </div>

            {stats && (
                <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                    gap: '20px',
                    marginBottom: '32px'
                }}>
                    {/* Total Unique Visitors */}
                    <div style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        padding: '24px',
                        borderRadius: '12px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}>
                        <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>
                            Total Unique Visitors
                        </div>
                        <div style={{ fontSize: '36px', fontWeight: 'bold', marginBottom: '4px' }}>
                            {formatNumber(stats.totalUniqueVisitors)}
                        </div>
                        <div style={{ fontSize: '12px', opacity: 0.8 }}>
                            All time
                        </div>
                    </div>

                    {/* Total Visits */}
                    <div style={{
                        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                        color: 'white',
                        padding: '24px',
                        borderRadius: '12px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}>
                        <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>
                            Total Page Visits
                        </div>
                        <div style={{ fontSize: '36px', fontWeight: 'bold', marginBottom: '4px' }}>
                            {formatNumber(stats.totalVisits)}
                        </div>
                        <div style={{ fontSize: '12px', opacity: 0.8 }}>
                            All time
                        </div>
                    </div>

                    {/* Last 24 Hours */}
                    <div style={{
                        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                        color: 'white',
                        padding: '24px',
                        borderRadius: '12px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}>
                        <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>
                            Last 24 Hours
                        </div>
                        <div style={{ fontSize: '36px', fontWeight: 'bold', marginBottom: '4px' }}>
                            {formatNumber(stats.uniqueVisitors24h)}
                        </div>
                        <div style={{ fontSize: '12px', opacity: 0.8 }}>
                            {formatNumber(stats.totalVisits24h)} visits
                        </div>
                    </div>

                    {/* Last 7 Days */}
                    <div style={{
                        background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                        color: 'white',
                        padding: '24px',
                        borderRadius: '12px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}>
                        <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>
                            Last 7 Days
                        </div>
                        <div style={{ fontSize: '36px', fontWeight: 'bold', marginBottom: '4px' }}>
                            {formatNumber(stats.uniqueVisitors7d)}
                        </div>
                        <div style={{ fontSize: '12px', opacity: 0.8 }}>
                            {formatNumber(stats.totalVisits7d)} visits
                        </div>
                    </div>

                    {/* Last 30 Days */}
                    <div style={{
                        background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                        color: 'white',
                        padding: '24px',
                        borderRadius: '12px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}>
                        <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>
                            Last 30 Days
                        </div>
                        <div style={{ fontSize: '36px', fontWeight: 'bold', marginBottom: '4px' }}>
                            {formatNumber(stats.uniqueVisitors30d)}
                        </div>
                        <div style={{ fontSize: '12px', opacity: 0.8 }}>
                            {formatNumber(stats.totalVisits30d)} visits
                        </div>
                    </div>
                </div>
            )}

            {/* Additional Info */}
            <div style={{
                background: 'white',
                padding: '20px',
                borderRadius: '12px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                border: '1px solid #e2e8f0'
            }}>
                <h3 style={{ marginTop: 0, marginBottom: '16px', color: '#1e293b' }}>
                    ℹ️ About Visitor Tracking
                </h3>
                <div style={{ color: '#64748b', lineHeight: '1.6' }}>
                    <p style={{ margin: '8px 0' }}>
                        • <strong>Unique Visitors:</strong> Counted based on device fingerprint (one visitor = one device)
                    </p>
                    <p style={{ margin: '8px 0' }}>
                        • <strong>Total Visits:</strong> Total number of page views across all visitors
                    </p>
                    <p style={{ margin: '8px 0' }}>
                        • <strong>Tracking:</strong> Anonymous visitors are tracked even if they don't sign up
                    </p>
                    <p style={{ margin: '8px 0' }}>
                        • <strong>Privacy:</strong> No personal information is stored, only device characteristics and IP addresses
                    </p>
                </div>
            </div>
        </div>
    );
};

export default VisitorStats;

